import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquare,
  ChevronUp,
  ChevronDown,
  Maximize2,
  Minimize2,
  RefreshCw,
  ExternalLink,
  Loader2,
  User,
  Image,
  FileIcon,
  Mic,
  Video,
  Download,
  Link2,
  Clock,
  UserPlus,
  History,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn, telegramClientLink } from '@/lib/utils';

interface PublicMessage {
  id: number;
  chatId: number;
  telegramMessageId: number;
  text: string | null;
  fromName: string | null;
  fromUsername: string | null;
  isOutgoing: boolean;
  telegramDate: string;
  markedPublicAt: string | null;
  mediaType: string | null;
  mediaUrl: string | null;
  fileId: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  mediaDuration: number | null;
  stickerEmoji: string | null;
  entities: string | null;
  chat: {
    id: number;
    title: string | null;
  };
  markedByAdmin?: {
    id: number;
    username: string;
  } | null;
}

interface ChatLink {
  id: number;
  chatId: number;
  orderId: number;
  linkedByAdminId: number;
  createdAt: string;
  linkedByAdmin?: {
    id: number;
    username: string;
  };
  chat?: {
    id: number;
    title: string | null;
    telegramChatId: number | null;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface OrderDialogPanelProps {
  orderId: number;
  user?: { username: string | null; telegramId: string | null };
  className?: string;
}

export function OrderDialogPanel({ orderId, user, className }: OrderDialogPanelProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState<PublicMessage[]>([]);
  const [chatLinks, setChatLinks] = useState<ChatLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLinks, setIsLoadingLinks] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/telegram-client/public-messages?orderId=${orderId}&limit=100`,
      );

      const result: ApiResponse<PublicMessage[]> = await response.json();
      if (result.success && result.data) {
        const sorted = result.data?.sort(
          (a, b) => new Date(a.telegramDate).getTime() - new Date(b.telegramDate).getTime()
        ) ?? [];
        setMessages(sorted);
      }
    } catch {
      // Ignore errors
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  const fetchChatLinks = useCallback(async () => {
    setIsLoadingLinks(true);
    try {
      const response = await fetch(
        `${API_URL}/api/telegram-client/orders/${orderId}/chat-links`,
      );

      const result: ApiResponse<ChatLink[]> = await response.json();
      if (result.success && result.data) {
        setChatLinks(result.data);
      }
    } catch {
      // Ignore errors
    } finally {
      setIsLoadingLinks(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchMessages();
    fetchChatLinks();
  }, [fetchMessages, fetchChatLinks]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Диалог с клиентом заказа в telegram-client админки; иначе общий клиент.
  const dmLink = user ? telegramClientLink(user) : null;
  const openTelegramClient = () => {
    window.open(dmLink ?? '/telegram-client', '_blank');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const date = new Date(msg.telegramDate).toLocaleDateString('ru-RU');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(msg);
    return acc;
  }, {} as Record<string, PublicMessage[]>);

  const totalChats = new Set(messages.map(m => m.chatId)).size;

  return (
    <>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn(
          'border rounded-xl overflow-hidden bg-card transition-all duration-200',
          isExpanded && 'fixed inset-4 z-50 shadow-2xl',
          className
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 p-0 h-auto hover:bg-transparent">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-primary" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-sm">Диалог</h3>
                <p className="text-xs text-muted-foreground">
                  {messages.length > 0
                    ? `${messages.length} сообщений`
                    : 'Нет сообщений'}
                  {totalChats > 1 && ` • ${totalChats} чата`}
                </p>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 ml-2 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-2 text-muted-foreground" />
              )}
            </Button>
          </CollapsibleTrigger>

          <div className="flex items-center gap-1">
            <TooltipProvider delayDuration={200}>
              {/* History button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHistory(true);
                    }}
                  >
                    <History className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>История привязок</TooltipContent>
              </Tooltip>

              {/* Refresh button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchMessages();
                      fetchChatLinks();
                    }}
                  >
                    <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Обновить</TooltipContent>
              </Tooltip>

              {/* Expand/Collapse button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                  >
                    {isExpanded ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isExpanded ? 'Свернуть' : 'Развернуть'}</TooltipContent>
              </Tooltip>

              {/* Open in Telegram client */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      openTelegramClient();
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{dmLink ? 'Открыть чат с клиентом' : 'Открыть Telegram'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Linked chats info bar */}
        {chatLinks.length > 0 && (
          <div className="px-4 py-2 bg-muted/30 border-b flex items-center gap-2 text-xs">
            <Link2 className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Привязан:</span>
            {chatLinks.map((link, i) => (
              <Badge key={link.id} variant="secondary" className="text-xs gap-1">
                <User className="h-3 w-3" />
                {link.chat?.title || `Чат #${link.chatId}`}
                {i < chatLinks.length - 1 && ','}
              </Badge>
            ))}
            {chatLinks[0]?.linkedByAdmin && (
              <span className="text-muted-foreground ml-auto flex items-center gap-1">
                <UserPlus className="h-3 w-3" />
                {chatLinks[0].linkedByAdmin.username}
                <span className="mx-1">•</span>
                <Clock className="h-3 w-3" />
                {formatShortDate(chatLinks[0].createdAt)}
              </span>
            )}
          </div>
        )}

        <CollapsibleContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">Нет публичных сообщений</p>
              <p className="text-xs mt-1">
                Привяжите чат и отметьте сообщения как публичные
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={openTelegramClient}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Открыть Telegram
              </Button>
            </div>
          ) : (
            <ScrollArea
              className={cn(
                'transition-all duration-200',
                isExpanded ? 'h-[calc(100vh-200px)]' : 'h-[400px]'
              )}
            >
              <div
                ref={scrollRef}
                className="p-4 space-y-4"
              >
                {Object.entries(groupedMessages).map(([date, msgs]) => (
                  <div key={date} className="space-y-2">
                    {/* Date separator */}
                    <div className="flex justify-center sticky top-0 z-10">
                      <Badge variant="secondary" className="text-xs font-normal px-3 py-1 shadow-sm">
                        {date}
                      </Badge>
                    </div>

                    {/* Messages */}
                    {msgs.map((msg) => (
                      <TelegramStyleMessage
                        key={msg.id}
                        message={msg}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </ScrollArea>
          ))}
        </CollapsibleContent>

        {/* Expanded overlay close button */}
        {isExpanded && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsExpanded(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </Collapsible>

      {/* History Sheet */}
      <Sheet open={showHistory} onOpenChange={setShowHistory}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              История диалога
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-4 overflow-y-auto">
            {/* Linked chats section */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Привязанные чаты
              </h4>
              {isLoadingLinks ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : (chatLinks.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Нет привязанных чатов
                </p>
              ) : (
                <div className="space-y-3">
                  {chatLinks.map((link) => (
                    <div
                      key={link.id}
                      className="p-3 rounded-lg border bg-muted/30"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {link.chat?.title || `Чат #${link.chatId}`}
                          </p>
                          {link.chat?.telegramChatId && (
                            <p className="text-xs text-muted-foreground">
                              ID: {link.chat.telegramChatId}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <UserPlus className="h-3 w-3" />
                          {link.linkedByAdmin?.username || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(link.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Messages stats */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Статистика сообщений
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border bg-muted/30 text-center">
                  <p className="text-2xl font-bold">{messages.length}</p>
                  <p className="text-xs text-muted-foreground">Публичных</p>
                </div>
                <div className="p-3 rounded-lg border bg-muted/30 text-center">
                  <p className="text-2xl font-bold">{totalChats}</p>
                  <p className="text-xs text-muted-foreground">Чатов</p>
                </div>
              </div>
            </div>

            {/* Who marked messages */}
            {messages.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Кто добавил сообщения
                </h4>
                <div className="space-y-2">
                  {[...new Map(messages.filter((m) => m.markedByAdmin).map((m) => [m.markedByAdmin!.id, m.markedByAdmin!])).values()].map((admin) => {
                    const count = messages.filter(m => m.markedByAdmin?.id === admin.id).length;
                    return (
                      <div
                        key={admin.id}
                        className="flex items-center justify-between p-2 rounded-lg border"
                      >
                        <span className="text-sm font-medium">@{admin.username}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Backdrop for expanded state */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </>
  );
}

// Telegram-style message bubble
interface TelegramStyleMessageProps {
  message: PublicMessage;
}

function TelegramStyleMessage({ message }: TelegramStyleMessageProps) {
  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {return `${bytes} B`;}
    if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const mediaType = message.mediaType;
  const isPhoto = mediaType === 'photo';
  const isVideo = mediaType === 'video';
  const isGif = mediaType === 'gif';
  const isVoice = mediaType === 'voice';
  const isVideoNote = mediaType === 'video_note';
  const isDocument = mediaType === 'document';
  const isSticker = mediaType === 'sticker';
  const isAudio = mediaType === 'audio';
  const hasMedia = mediaType && mediaType !== 'unknown';

  return (
    <div
      className={cn(
        'flex',
        message.isOutgoing ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl overflow-hidden shadow-sm',
          hasMedia && !message.text ? '' : 'px-3 py-2',
          message.isOutgoing
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-card border rounded-bl-md'
        )}
      >
        {/* Sender name for incoming messages */}
        {!message.isOutgoing && message.fromName && (
          <p className="text-xs font-semibold mb-1 text-blue-500">
            {message.fromName}
            {message.fromUsername && (
              <span className="font-normal text-muted-foreground ml-1">
                @{message.fromUsername}
              </span>
            )}
          </p>
        )}

        {/* Media content */}
        {hasMedia && (
          <MediaContent
            message={message}
            isPhoto={isPhoto}
            isVideo={isVideo}
            isGif={isGif}
            isVoice={isVoice}
            isVideoNote={isVideoNote}
            isDocument={isDocument}
            isSticker={isSticker}
            isAudio={isAudio}
            isOutgoing={message.isOutgoing}
            formatDuration={formatDuration}
            formatFileSize={formatFileSize}
          />
        )}

        {/* Text content */}
        {message.text && (
          <p className={cn(
            'text-sm whitespace-pre-wrap break-words',
            hasMedia && 'px-3 pb-2 pt-1'
          )}>
            {message.text}
          </p>
        )}

        {/* Time and admin badge */}
        <div className={cn(
          'flex items-center justify-end gap-1.5 mt-1',
          message.isOutgoing ? 'text-white/70' : 'text-muted-foreground',
          hasMedia && !message.text && 'px-3 pb-2'
        )}>
          {message.markedByAdmin && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="secondary"
                    className={cn(
                      'text-[9px] px-1.5 py-0 h-4',
                      message.isOutgoing && 'bg-white/20 text-white border-0'
                    )}
                  >
                    @{message.markedByAdmin.username}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Опубликовал {message.markedPublicAt && formatTime(message.markedPublicAt)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="text-[10px]">{formatTime(message.telegramDate)}</span>
        </div>
      </div>
    </div>
  );
}

// Media content component
interface MediaContentProps {
  message: PublicMessage;
  isPhoto: boolean;
  isVideo: boolean;
  isGif: boolean;
  isVoice: boolean;
  isVideoNote: boolean;
  isDocument: boolean;
  isSticker: boolean;
  isAudio: boolean;
  isOutgoing: boolean;
  formatDuration: (seconds: number) => string;
  formatFileSize: (bytes: number) => string;
}

function MediaContent({
  message,
  isPhoto,
  isVideo,
  isGif,
  isVoice,
  isVideoNote,
  isDocument,
  isSticker,
  isAudio,
  isOutgoing,
  formatDuration,
  formatFileSize,
}: MediaContentProps) {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const chatId = message.chatId;
  const telegramMessageId = message.telegramMessageId;

  useEffect(() => {
    if (!chatId || !telegramMessageId) {return;}
    if (!isPhoto && !isVideo && !isGif && !isVideoNote && !isVoice) {return;}

    const loadMedia = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const response = await fetch(
          `${API_URL}/api/telegram-client/chats/${chatId}/messages/${telegramMessageId}/media`,
        );

        if (!response.ok) {throw new Error('Failed');}

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setMediaUrl(url);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadMedia();

    return () => {
      if (mediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, telegramMessageId, isPhoto, isVideo, isGif, isVideoNote, isVoice]);

  const textColorClass = isOutgoing ? 'text-white/80' : 'text-muted-foreground';

  // Photo
  if (isPhoto) {
    if (isLoading) {
      return (
        <div className="w-full h-40 flex items-center justify-center bg-muted/20 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (error || !mediaUrl) {
      return (
        <div className={cn('flex items-center gap-2 px-3 py-2 text-sm', textColorClass)}>
          <Image className="h-4 w-4" />
          <span className="italic">Фото</span>
        </div>
      );
    }
    return (
      <img
        src={mediaUrl}
        alt=""
        className="max-w-full max-h-72 object-contain rounded-lg"
      />
    );
  }

  // Video / GIF
  if (isVideo || isGif) {
    if (isLoading) {
      return (
        <div className="w-full h-40 flex items-center justify-center bg-muted/20 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (error || !mediaUrl) {
      return (
        <div className={cn('flex items-center gap-2 px-3 py-2 text-sm', textColorClass)}>
          <Video className="h-4 w-4" />
          <span className="italic">{isGif ? 'GIF' : 'Видео'}</span>
          {message.mediaDuration && (
            <span className="text-xs">{formatDuration(message.mediaDuration)}</span>
          )}
        </div>
      );
    }
    return (
      <video
        src={mediaUrl}
        controls={!isGif}
        autoPlay={isGif}
        loop={isGif}
        muted={isGif}
        playsInline
        className="max-w-full max-h-72 object-contain rounded-lg"
      />
    );
  }

  // Video note
  if (isVideoNote) {
    if (isLoading) {
      return (
        <div className="w-36 h-36 rounded-full flex items-center justify-center bg-muted/20">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (error || !mediaUrl) {
      return (
        <div className={cn('flex items-center gap-2 px-3 py-2 text-sm', textColorClass)}>
          <Video className="h-4 w-4" />
          <span className="italic">Видеосообщение</span>
        </div>
      );
    }
    return (
      <div className="w-36 h-36 rounded-full overflow-hidden border-2 border-blue-500">
        <video
          src={mediaUrl}
          controls
          playsInline
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Voice
  if (isVoice) {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Загрузка...</span>
        </div>
      );
    }
    if (error || !mediaUrl) {
      return (
        <div className={cn('flex items-center gap-2 px-3 py-2 text-sm', textColorClass)}>
          <Mic className="h-4 w-4" />
          <span className="italic">Голосовое</span>
          {message.mediaDuration && (
            <span className="text-xs">{formatDuration(message.mediaDuration)}</span>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Mic className="h-4 w-4" />
        <audio src={mediaUrl} controls className="h-8 max-w-[180px]" />
        {message.mediaDuration && (
          <span className={cn('text-xs', textColorClass)}>{formatDuration(message.mediaDuration)}</span>
        )}
      </div>
    );
  }

  // Sticker
  if (isSticker) {
    return (
      <div className="px-3 py-2 text-3xl">
        {message.stickerEmoji || '🎴'}
      </div>
    );
  }

  // Audio
  if (isAudio) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <FileIcon className="h-4 w-4" />
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate">{message.fileName || 'Аудио'}</p>
          {message.mediaDuration && (
            <p className={cn('text-xs', textColorClass)}>{formatDuration(message.mediaDuration)}</p>
          )}
        </div>
      </div>
    );
  }

  // Document
  if (isDocument) {
    const handleDownload = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/telegram-client/chats/${chatId}/messages/${telegramMessageId}/media`,
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = message.fileName || 'file';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch {
        // Ignore
      }
    };

    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <div className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center',
          isOutgoing ? 'bg-white/20' : 'bg-blue-500/10'
        )}>
          <FileIcon className={cn('h-5 w-5', isOutgoing ? 'text-white' : 'text-blue-500')} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate font-medium">{message.fileName || 'Файл'}</p>
          {message.fileSize && (
            <p className={cn('text-xs', textColorClass)}>{formatFileSize(message.fileSize)}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', isOutgoing && 'hover:bg-white/20')}
          onClick={handleDownload}
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Unknown
  return (
    <div className={cn('flex items-center gap-2 px-3 py-2 text-sm italic', textColorClass)}>
      <FileIcon className="h-4 w-4" />
      <span>Медиа</span>
    </div>
  );
}
